import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { IconButton, Typography } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';

import IconDelete from 'src/assets/icons/Delete';
import { IconBookmarkEmpty } from 'src/assets/icons/BookmarkEmpty';
import { IconEditPencil } from 'src/assets/icons/IconEditPencil';
import { formatPersonaDescription } from 'src/data/persona';
import { PersonaAvatar } from '../PersonaAvatar';
import { TruncatedTypography } from '../TruncatedTypography';
import { PainsPositioner } from './components/PainsPositioner';
import { useStyles } from './useStyles';

export const PersonaDetailsPanel = ({
  className,
  persona,
  onEdit,
  onDelete,
  onSave,
  displayActions,
  disabledActions,
  customActionsSlot,
}) => {
  const styles = useStyles();

  const { name, avatarType, painPoints, isSuggested } = persona || {};
  const description = useMemo(() => formatPersonaDescription(persona), [persona]);

  const handleEditClick = useCallback(() => {
    if (onEdit) {
      onEdit(persona);
    }
  }, [onEdit, persona]);

  const handleDeleteClick = useCallback(() => {
    if (onDelete) {
      onDelete(persona);
    }
  }, [onDelete, persona]);

  const handleSaveClick = useCallback(() => {
    if (onSave) {
      onSave(persona);
    }
  }, [onSave, persona]);

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.content}>
        <div className={styles.header}>
          <PersonaAvatar
            className={styles.avatar}
            avatarType={avatarType}
          />

          <div className={styles.texts}>
            <TruncatedTypography className={styles.title}>{name}</TruncatedTypography>
            <Typography className={styles.description}>{description}</Typography>
          </div>
        </div>

        {painPoints?.length > 0 && (
          <PainsPositioner
            className={styles.pains}
            pains={painPoints}
          />
        )}

        {displayActions && (
          <div className={styles.actions}>
            {isSuggested && (
              <Tooltip title="Add to my audiences">
                <IconButton
                  className={styles.button}
                  onClick={handleSaveClick}
                >
                  <IconBookmarkEmpty />
                </IconButton>
              </Tooltip>
            )}

            <IconButton
              className={styles.button}
              onClick={handleEditClick}
              disabled={disabledActions}
            >
              <IconEditPencil className={styles.iconEditPersona} />
            </IconButton>

            <IconButton
              className={styles.button}
              onClick={handleDeleteClick}
              disabled={disabledActions}
            >
              <IconDelete />
            </IconButton>
          </div>
        )}
      </div>

      {customActionsSlot && <div className={styles.customActions}>{customActionsSlot}</div>}
    </div>
  );
};

PersonaDetailsPanel.propTypes = {
  className: PropTypes.string,
  persona: PropTypes.shape({
    isSuggested: PropTypes.bool,
  }).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSave: PropTypes.func,
  displayActions: PropTypes.bool,
  disabledActions: PropTypes.bool,
  customActionsSlot: PropTypes.node,
};
PersonaDetailsPanel.defaultProps = {
  className: '',
  onEdit: null,
  onDelete: null,
  onSave: null,
  displayActions: false,
  disabledActions: false,
  customActionsSlot: null,
};
